import { KeyboardEventHandler, useState } from 'react';
import styles from './MenuItem.module.scss';
import PureLink from '../Link/PureLink';
import { RightArrow } from '../../../lib/icons/RightArrow';
import { classes } from '../../../lib/utils/cssUtils';
import { useDevice } from '../../../lib/hooks/device';
import { useAttributeLabel } from '../../../lib/hooks/labels';
import { ATTRIBUTES } from '../../../lib/constants/attributes';
import { HEADER_LABEL_KEYS } from '../../../lib/constants/labelKeys';
import SubItem from './SubItem';
import { MenuItemProps } from './MenuItem.types';
import WithDevice from '../WithDevice/WithDevice';
import { useId } from '../../../lib/hooks/useId';
import RefreshLoader from '../RefreshLoader/RefreshLoader';
import { Role, useRole } from '../../../lib/context/SessionContext';

const MenuItem = ({
  label,
  link,
  icon,
  className,
  variant = 'primary',
  subItems = [],
  onClick = () => {},
  loading,
}: MenuItemProps) => {
  const [isMobileCollapsed, setIsMobileCollapsed] = useState(true);
  const expandSubmenuLabel = useAttributeLabel(
    ATTRIBUTES.ARIA_LABEL,
    HEADER_LABEL_KEYS.MOBILE_MENU_EXPAND_SUBMENU_ARIA_LABEL,
  );
  const device = useDevice();
  const role = useRole();
  const nestedListId = useId('nested-list-');

  const [elementFocused, setElementFocused] = useState<number | undefined>(undefined);

  const handleKeyboardEvent: KeyboardEventHandler<HTMLAnchorElement> = event => {
    if (event.key === 'Tab' && elementFocused !== undefined && elementFocused < subItems?.length) {
      event.preventDefault();
      setElementFocused(prev => (prev === undefined ? 0 : prev + 1));
    }
  };

  return (
    <li
      className={classes(styles.item, className, {
        [styles.profileItem]: variant === 'profile' && role !== Role.LEARNER_MANAGER,
        [styles.profileItemBorder]: role !== Role.LEARNER_MANAGER && variant === 'profile',
      })}
    >
      <div className={classes(styles.itemRow, { [styles.active]: device !== 'desktop' && !isMobileCollapsed })}>
        <PureLink className={styles.link} href={link} onKeyDown={handleKeyboardEvent} onClick={onClick}>
          {loading && <RefreshLoader />}
          {icon || null}
          {typeof label === 'string' ? (
            <span
              className={classes(styles.linkText, { [styles.profileLinkText]: variant === 'profile' })}
              data-text={label}
            >
              {label}
            </span>
          ) : (
            label
          )}
        </PureLink>
        <WithDevice mobile tablet>
          {subItems.length ? (
            <button
              {...expandSubmenuLabel}
              aria-expanded={!isMobileCollapsed}
              aria-controls={nestedListId}
              className={styles.arrowButton}
              onClick={() => setIsMobileCollapsed(prev => !prev)}
            >
              <RightArrow className={classes(styles.arrow, { [styles.activeArrow]: !isMobileCollapsed })} />
            </button>
          ) : null}
        </WithDevice>
      </div>
      {subItems.length ? (
        <ul
          id={nestedListId}
          className={classes(styles.submenu, {
            [styles.collapsedMenu]: device !== 'desktop' && isMobileCollapsed,
            [styles.expandedMenu]: device !== 'desktop' && !isMobileCollapsed,
          })}
        >
          {subItems.map(({ id, ...rest }, index) => (
            <SubItem
              keyboardEventHandler={handleKeyboardEvent}
              key={id}
              {...rest}
              focusActive={index === elementFocused}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
};

export default MenuItem;
